import React from 'react';
import {
  FunctionField,
  Datagrid,
  usePermissions,
  Filter,
  TextInput,
  ListController,
  ListContextProvider,
  Button,
  useTranslate,
} from 'react-admin';
import PeopleIcon from '@material-ui/icons/People';
import { Typography } from '@material-ui/core';
import { withStyles, createStyles, makeStyles } from '@material-ui/core/styles';
import { ListView } from '~/layout';
import { DateTimeInput } from '~/components/ra';
import { SelectInput } from 'react-admin';
import { TextField } from '~/components';
import { ProfileTag } from '~/types/profileTags';
import palette from '~/theme/palette';
import { history } from '~/App';
import { Draft } from '~/types/Draft';
import { time } from '~/utils';

const tKey = 'resources.individuals.draftList';
const label = (key: string) => `${tKey}.fields.${key}`;

const listBodyStyles = (theme: any) => {
  return createStyles({
    content: {
      fontSize: 14,
      paddingLeft: 16,
      paddingRight: 16,
      [theme.breakpoints.up('sm')]: {
        paddingLeft: 24,
        paddingRight: 24,
      },
      paddingTop: 16,
      paddingBottom: 16,
    },
  });
};

const DraftsList = (props: any) => {
  const { permissions } = usePermissions();
  const classes = useStyles();
  const translate = useTranslate();
  if (!permissions) return null;

  return (
    <ListController {...props}>
      {(controllerProps: any) => {
        return (
          <ListContextProvider value={controllerProps}>
            <ListView
              title={
                <div className={classes.headline}>
                  <Typography variant='h2'>{translate(`${tKey}.title`)}</Typography>
                </div>
              }
              filters={<DraftsFilters />}
              {...controllerProps}
              pagination={controllerProps.loading || controllerProps.total === 0 ? null : undefined}
              bulkActionButtons={false}
              actions={
                <Button
                  label={translate('resources.individuals.name', 2)}
                  onClick={() => history.push('/individuals')}
                >
                  <PeopleIcon />
                </Button>
              }
            >
              <ListBody />
            </ListView>
          </ListContextProvider>
        );
      }}
    </ListController>
  );
};

const ListBody = withStyles(listBodyStyles)((props: any) => {
  const translate = useTranslate();
  const t = (key: string): string => translate(label(key));

  const { total, loading, classes } = props;
  if (loading) {
    return <div className={classes.content}>{t('searchInProgress')}</div>;
  } else if (total === 0) {
    return <div className={classes.content}>{t('noResultsFound')}</div>;
  }
  return (
    <>
      <Datagrid
        {...props}
        rowClick={(_id: any, _basePath: any, record: ProfileTag) =>
          history.push(`/individuals/${record?.id}.draft/show/profile`)
        }
        size='small'
        selectedIds={null}
      >
        <FunctionField
          label={t('title')}
          sortable={false}
          render={(record: any) => {
            return record?.title;
          }}
        />
        <FunctionField
          label={t('createdAt')}
          sortable={false}
          render={(record: Draft) => {
            const createdAt = time(record.createdAt);
            return <TextField label={createdAt.format('LL')}>{createdAt.format('LTS')}</TextField>;
          }}
        />
        <FunctionField
          label={t('updatedAt')}
          sortable={false}
          render={(record: Draft) => {
            const updatedAt = time(record.updatedAt);
            return <TextField label={updatedAt.format('LL')}>{updatedAt.format('LTS')}</TextField>;
          }}
        />
        <FunctionField
          label={t('type')}
          sortable={false}
          render={(record: Draft) => {
            return record.isUpdateDraft ? (
              <TextField style={{ color: palette.warning.main }}>{t('updateType')}</TextField>
            ) : (
              <TextField style={{ color: palette.success.main }}>{t('createType')}</TextField>
            );
          }}
        />
      </Datagrid>
    </>
  );
});

const DraftsFilters = (props: any) => {
  const classes = useStyles();
  const { permissions } = usePermissions();
  const hasIndividualPermissionEdit = permissions?.includes('individual.update');
  const hasIndividualPermissionCreate = permissions?.includes('individual.create');

  const typeChoices = [
    { id: '', name: label('allDrafts') },
    ...(hasIndividualPermissionCreate ? [{ id: 'false', name: label('onlyCreate') }] : []),
    ...(hasIndividualPermissionEdit ? [{ id: 'true', name: label('onlyUpdate') }] : []),
  ];

  return (
    <Filter {...props} className={classes.form}>
      <DateTimeInput
        className={classes.formControl}
        label={label('createdAtFrom')}
        source='createdFrom'
        options={{ format: 'LL LT', clearable: true }}
        alwaysOn
      />
      <DateTimeInput
        className={classes.formControl}
        label={label('createdAtTo')}
        source='createdTo'
        options={{ format: 'LL LT', clearable: true }}
        alwaysOn
      />
      <SelectInput
        className={classes.formControl}
        label={label('type')}
        source='isUpdateDraft'
        allowEmpty={false}
        choices={typeChoices}
        alwaysOn
      />
      <TextInput
        className={classes.formControl}
        label={label('objectId')}
        source='objectId'
        alwaysOn
      />
    </Filter>
  );
};

const useStyles = makeStyles((theme) => ({
  form: {
    '& > div': {
      '& > div:last-child': {
        width: theme.spacing(1),
      },
    },
  },
  formControl: {
    width: 200,
  },
  headline: {
    marginTop: '8px',
    display: 'flex',
  },
  multiselect: {
    width: 300,
    '& > .MuiInputBase-root ': {
      minHeight: 46,
      '& > .MuiSelect-root': {
        paddingTop: 12,
        paddingBottom: 0,
        marginBottom: -4,
      },
    },
  },
}));

export default DraftsList;
